import { Router } from '@angular/router';
import { Injectable } from '@angular/core';

import { RouteNames, NamedRoute, NamedRoutes, NamedRouteParams, NamedRouterNavigationExtras } from './types';

@Injectable()
export class NamedRouter extends Router {
  private _routes: NamedRoutes = [];

  private _routeMap = new Map<RouteNames, string[]>();

  get routes() {
    return this._routeMap;
  }

  public async setRoutes(routes: NamedRoutes) {
    this._routes = [...routes];
    for (const route of routes) {
      await this._buildRoutes(route, ['/']);
    }
  }

  private async _buildRoutes(route: NamedRoute, path: string[]) {
    if (route.path?.length) {
      path.push(...route.path.split('/'));
    }

    const routeName = route.name;
    if (routeName) {
      const mappedRoute = this._routeMap.get(routeName);
      if (mappedRoute) {
        const existingPath = mappedRoute.join('/');
        const newPath = path.join('/');
        throw new Error(
          `Duplicate route for '${routeName}': Two route paths have the same names. See route: '${existingPath}' and '${newPath}'.`,
        );
      }
      this._routeMap.set(routeName, [...path]);
    }

    if (route.children) {
      route.children.forEach((child) => this._buildRoutes(child, [...path]));
    }

    if (route.loadChildren) {
      const children = await route.loadChildren();
      children.forEach((child) => this._buildRoutes(child, [...path]));
    }
  }

  getRoute(routeName: RouteNames, params: NamedRouteParams = {}): string[] {
    const path = this._routeMap.get(routeName);
    if (!path) {
      throw new Error(`Unexpected route name '${routeName}'.`);
    }

    const pathParams = path.filter((pathPart) => pathPart.startsWith(':'));
    const paramsList = Object.keys(params);

    if (pathParams.length > paramsList.length) {
      throw new Error(`Not enough parameters provided for route '${routeName}'.`);
    }

    if (pathParams.length < paramsList.length) {
      throw new Error(`Too many parameters provided for route '${routeName}'.`);
    }

    return path.map((pathPart) => {
      const isParamPart = pathPart.startsWith(':');
      if (isParamPart) {
        const paramName = pathPart.replace(':', '');
        return params[paramName].toString();
      }

      return pathPart;
    });
  }

  navigateByName(name: RouteNames, extras?: NamedRouterNavigationExtras) {
    this.navigate(this.getRoute(name, extras?.params), extras);
  }
}
